import DraggableButton from "ui/automation/language-dragable-button";
import { DndProvider } from "react-dnd";
import Switch from "react-switch";
import { HTML5Backend } from "react-dnd-html5-backend";
import { SvgAudioLibrary } from "ui/svgs";
import { Tooltip, Select, Skeleton, Button, Form, Modal } from "antd";
import { useEffect, useMemo, useState } from "react";
import { getIpSpeakers } from "pages/service";
import { AdvancedAISettingType } from "lib/types";
import { SvgToastInfo } from "ui/svgs";
import TextArea from "antd/es/input/TextArea";
import { generateVgInstruction } from "pages/dashboard/automation/service";
import Schedule from "ui/automation/schedule";
const langsText = ["English", "Spanish", "Japanese", "Korean"];
export default function VirtualGuard(props: {
  camera;
  advancedAISetting: AdvancedAISettingType;
  setAdvancedAISetting: (data: AdvancedAISettingType) => void;
}) {
  const [instructionVisible, setInstructionVisible] = useState(false);
  const { camera, advancedAISetting, setAdvancedAISetting } = props;
  const [buttons, setButtons] = useState(() => {
    const languages = advancedAISetting?.virtualGuard?.languages || [];
    const unSelectedButtons =
      langsText.filter((button) => !languages?.includes(button)) || [];
    return [...languages, ...unSelectedButtons];
  });
  const [speakerOpions, setSpeakerOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [speakerOpionsLoading, setSpeakerOpionsLoading] = useState(true);
  const siteId = useMemo(() => {
    return +camera?.siteId;
  }, [camera]);
  useEffect(() => {
    if (siteId === null && siteId === undefined) {
      return;
    }
    const prepareSpeakers = async () => {
      try {
        setSpeakerOpionsLoading(true);
        const data = await getIpSpeakers(camera?.orgId, siteId);
        setSpeakerOpionsLoading(false);
        const speakers = data?.data;
        setSpeakerOptions(
          speakers?.map((e) => ({
            value: e?.id,
            label: e?.name,
          })),
        );
      } catch (error) {
        console.error("Error in getting speakers", error);
      }
    };
    prepareSpeakers();
  }, [siteId]);

  const onDragMove = (fromIndex, toIndex) => {
    const newButtons = [...buttons];
    const [movedButton] = newButtons.splice(fromIndex, 1);
    newButtons.splice(toIndex, 0, movedButton);
    console.log("after dragMove", newButtons);
    const languages = advancedAISetting?.virtualGuard?.languages || [];
    // sort by buttons
    languages?.sort((a, b) => {
      const aIndex = newButtons.findIndex((button) => button === a);
      const bIndex = newButtons.findIndex((button) => button === b);
      return aIndex - bIndex;
    });
    setAdvancedAISetting({
      ...advancedAISetting,
      virtualGuard: {
        ...advancedAISetting?.virtualGuard,
        languages,
      },
    });
    setButtons(newButtons);
  };
  const handleSpeakerChange = (value) => {
    setAdvancedAISetting({
      ...advancedAISetting,
      virtualGuard: {
        ...advancedAISetting?.virtualGuard,
        speakerIds: value,
      },
    });
  };
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const disableGenerate = Form.useWatch(
    (values) =>
      !(values.triggerCondition && values.messageIntention && values.courtesy),
    form,
  );
  useEffect(() => {
    form.setFieldsValue(advancedAISetting?.virtualGuard.customPromptCfg);
  }, [advancedAISetting?.virtualGuard]);
  // set visible to true if customPrompt is set, but need user to set invisible
  useEffect(() => {
    if (advancedAISetting?.virtualGuard?.customPrompt) {
      setInstructionVisible(true);
    }
  }, [advancedAISetting?.virtualGuard?.customPrompt]);
  function onDelete() {
    setAdvancedAISetting({
      ...advancedAISetting,
      virtualGuard: {
        ...advancedAISetting?.virtualGuard,
        customPrompt: "",
        customPromptCfg: form.getFieldsValue(),
      },
    });
  }
  async function onGenerateInstruction() {
    setErrorMessage("");
    setLoading(true);
    const values = form.getFieldsValue();
    try {
      const res = await generateVgInstruction(
        values.courtesy,
        values.messageIntention,
        values.triggerCondition,
      );
      if (res.status === 200 && res.data.valid) {
        setAdvancedAISetting({
          ...advancedAISetting,
          virtualGuard: {
            ...advancedAISetting?.virtualGuard,
            customPrompt: res.data.customPrompt,
            customPromptCfg: values,
          },
        });
      } else {
        setErrorMessage(
          res.data.reason ? res.data.reason : "Failed to generate instruction",
        );
      }
    } finally {
      setLoading(false);
    }
  }
  function onOpenCustomInstruction(checked) {
    if (checked) {
      setInstructionVisible(true);
    } else {
      if (advancedAISetting?.virtualGuard?.customPrompt) {
        Modal.confirm({
          title: "Are you sure you want to clear the custom instruction?",
          content: "You can regenerate it once it cleared.",
          okButtonProps: { className: "primaryButton bordered danger" },
          cancelButtonProps: { className: "primaryButton bordered" },
          okText: "Delete",
          cancelText: "Cancel",
          onOk: () => {
            setInstructionVisible(false);
            setAdvancedAISetting({
              ...advancedAISetting,
              virtualGuard: {
                ...advancedAISetting?.virtualGuard,
                customPrompt: "",
                customPromptCfg: form.getFieldsValue(),
              },
            });
          },
        });
      } else {
        setInstructionVisible(false);
      }
    }
  }
  if (speakerOpionsLoading) {
    return (
      <div className="w-full h-full px-6 py-6">
        <Skeleton />
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: "16px" }}>
      <div className="bg-[#fafafa] p-6 rounded-2xl flex-1">
        {!camera?.capabilities?.speak && speakerOpions?.length === 0 && (
          <p className="text-[#717171] mt-6 break-normal">
            Add an IP speaker or use a camera with speaker support to enable
            this feature
          </p>
        )}
        {camera?.capabilities?.speak && (
          <div className="flex items-center justify-between py-3 rounded-2xl bg-[#fafafa]">
            <span className="font-semibold text-base">
              Virtual Guard with Camera Speaker
            </span>
            <Switch
              onChange={() => {
                setAdvancedAISetting({
                  ...advancedAISetting,
                  virtualGuard: {
                    ...advancedAISetting?.virtualGuard,
                    cameraSpeakerEnabled:
                      !advancedAISetting?.virtualGuard?.cameraSpeakerEnabled,
                  },
                });
              }}
              checked={advancedAISetting?.virtualGuard?.cameraSpeakerEnabled}
              uncheckedIcon={false}
              checkedIcon={false}
              onColor="#3E83F6"
              offColor="#fff"
              handleDiameter={20}
              offHandleColor="#9E9E9E"
              className="border border-zinc-200"
              height={32}
              width={52}
            />
          </div>
        )}

        {(speakerOpions?.length > 0 ||
          advancedAISetting?.virtualGuard?.speakerIds?.length > 0) && (
          <div className="py-3 rounded-2xl bg-[#fafafa]">
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-base">
                  Virtual Guard with IP Speaker
                </span>
                <Tooltip title="Requires an IP Speaker. Connect one to enable this feature.">
                  <div className="hover:bg-gray-100 hover-bg-gray-100 w-4 h-4 cursor-pointer">
                    <SvgToastInfo width="16" className="fill-neutral-500" />
                  </div>
                </Tooltip>
              </div>

              <Switch
                onChange={() => {
                  const setting = {
                    ...advancedAISetting,
                    virtualGuard: {
                      ...advancedAISetting?.virtualGuard,
                      ipSpeakerEnabled:
                        !advancedAISetting?.virtualGuard?.ipSpeakerEnabled,
                    },
                  };
                  advancedAISetting?.virtualGuard?.ipSpeakerEnabled &&
                    (setting.virtualGuard.speakerIds = []);
                  setAdvancedAISetting(setting);
                }}
                checked={advancedAISetting?.virtualGuard?.ipSpeakerEnabled}
                uncheckedIcon={false}
                checkedIcon={false}
                onColor="#3E83F6"
                offColor="#fff"
                handleDiameter={20}
                offHandleColor="#9E9E9E"
                className="border border-zinc-200"
                height={32}
                width={52}
              />
            </div>
            {advancedAISetting?.virtualGuard?.ipSpeakerEnabled && (
              <>
                <div className="mt-3 rounded-2xl bg-[#fafafa]">
                  <Select
                    mode="multiple"
                    popupClassName="advancedAI-select-dropdown"
                    loading={true}
                    style={{ width: "100%" }}
                    options={speakerOpions}
                    className="speakerSelect"
                    value={advancedAISetting?.virtualGuard?.speakerIds}
                    onChange={handleSpeakerChange}
                    placeholder="All speakers are enabled by default."
                    suffixIcon={null}
                    labelRender={(label) => (
                      <div className="flex gap-2 pr-2">
                        <SvgAudioLibrary
                          width={18}
                          className="fill-[#717171]"
                        />
                        {label.label}
                      </div>
                    )}
                  />
                </div>
              </>
            )}
          </div>
        )}
        {((speakerOpions?.length > 0 ||
          advancedAISetting?.virtualGuard?.speakerIds?.length > 0) &&
          advancedAISetting?.virtualGuard?.ipSpeakerEnabled) ||
        (camera?.capabilities?.speak &&
          advancedAISetting?.virtualGuard?.cameraSpeakerEnabled) ? (
          <>
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-base">
                  Custom Instruction Status
                </span>
              </div>
              <Switch
                onChange={onOpenCustomInstruction}
                checked={instructionVisible}
                checkedIcon={false}
                uncheckedIcon={false}
                className="border border-zinc-200"
                onColor="#3E83F6"
                offColor="#fff"
                handleDiameter={20}
                offHandleColor="#9E9E9E"
                height={32}
                width={52}
              />
            </div>
            {instructionVisible && (
              <>
                <div style={{ fontSize: "14px", fontWeight: "400" }}>
                  Detection only occurs when an alert is reported by the NVR.
                  Define how Virtual Guard should respond when triggered.
                </div>
                <Form layout="vertical" form={form}>
                  <Form.Item label="Trigger Condition" name="triggerCondition">
                    <TextArea
                      placeholder={`(Under what situation you need to trigger virtual guard broadcast?) e.g.
1.When the door is left open and nobody is closing it.
2.When someone is running in the sight.`}
                      count={{
                        max: 300,
                        show: true,
                        exceedFormatter: (value, config) => {
                          return value.substring(0, config.max);
                        },
                      }}
                    />
                  </Form.Item>
                  <Form.Item label="Message Intention" name="messageIntention">
                    <TextArea
                      placeholder={`(What is the purpose of the broadcasting message when virtual guard is triggered?) e.g.
1.On trigger condition 1, remind someone to close the door.
2.On trigger condition 2, make the running person stop running.`}
                      count={{
                        max: 300,
                        show: true,
                        exceedFormatter: (value, config) => {
                          return value.substring(0, config.max);
                        },
                      }}
                    />
                  </Form.Item>
                  <Form.Item label="Courtesy" name="courtesy">
                    <TextArea
                      placeholder={`(How polite you want the virtual guard to be?) e.g.
1.Be polite and gentle at the trigger condition 1.
2.Be strict at the trigger condition 2.`}
                      count={{
                        max: 300,
                        show: true,
                        exceedFormatter: (value, config) => {
                          return value.substring(0, config.max);
                        },
                      }}
                    />
                  </Form.Item>
                  <Form.Item help={errorMessage}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Button
                        loading={loading}
                        disabled={
                          !!advancedAISetting?.virtualGuard?.customPrompt ||
                          disableGenerate
                        }
                        onClick={onGenerateInstruction}
                        type="primary"
                        className={`primaryButton ${!advancedAISetting?.virtualGuard?.customPrompt && !disableGenerate && "bordered"}`}
                      >
                        Generate Instruction
                      </Button>
                      <Button
                        type="primary"
                        className={`primaryButton ${advancedAISetting?.virtualGuard.customPrompt && "bordered danger"}`}
                        onClick={onDelete}
                        disabled={!advancedAISetting?.virtualGuard.customPrompt}
                      >
                        Clear Instruction
                      </Button>
                    </div>
                  </Form.Item>
                </Form>
                {advancedAISetting?.virtualGuard?.customPrompt && (
                  <div
                    style={{
                      backgroundColor: "rgba(245, 245, 245, 1)",
                      borderRadius: "16px",
                      padding: "16px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        marginBottom: "8px",
                      }}
                    >
                      Generated Instruction:
                    </p>
                    <p style={{ whiteSpace: "break-spaces" }}>
                      {advancedAISetting?.virtualGuard?.customPrompt}
                    </p>
                  </div>
                )}
              </>
            )}
            <div
              style={{
                borderTop: "1px solid rgba(225, 225, 225, 1)",
                height: "1px",
                width: "100%",
                marginTop: "12px",
              }}
            />
            <p className="font-semibold mt-3">Languages to Broadcast</p>
            <p className="mt-1 text-[#222222] text-sm">
              Drag to reorder. Each selected language will play twice insequence
              before the next.
            </p>
            <DndProvider backend={HTML5Backend}>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                {buttons.map((button, index) => {
                  const isSelected =
                    advancedAISetting?.virtualGuard?.languages?.includes(
                      button,
                    );
                  const disabled =
                    advancedAISetting?.virtualGuard?.languages?.length === 3 &&
                    !advancedAISetting?.virtualGuard?.languages?.includes(
                      button,
                    );
                  return (
                    <DraggableButton
                      key={button}
                      id={button}
                      text={button}
                      index={index}
                      canDrag={isSelected}
                      onDragMove={onDragMove}
                      disabled={disabled}
                      onCheck={() => {
                        if (isSelected) {
                          setAdvancedAISetting({
                            ...advancedAISetting,
                            virtualGuard: {
                              ...advancedAISetting?.virtualGuard,
                              languages:
                                advancedAISetting?.virtualGuard?.languages?.filter(
                                  (l) => l !== button,
                                ),
                            },
                          });
                        } else {
                          const languages = [
                            ...(advancedAISetting?.virtualGuard?.languages ||
                              []),
                            button,
                          ];
                          // sort by buttons
                          languages?.sort((a, b) => {
                            const aIndex = buttons.findIndex(
                              (button) => button === a,
                            );
                            const bIndex = buttons.findIndex(
                              (button) => button === b,
                            );
                            return aIndex - bIndex;
                          });

                          setAdvancedAISetting({
                            ...advancedAISetting,
                            virtualGuard: {
                              ...advancedAISetting?.virtualGuard,
                              languages,
                            },
                          });
                        }
                      }}
                    />
                  );
                })}
              </div>
            </DndProvider>
          </>
        ) : null}
      </div>
      {(!!camera?.capabilities?.speak || !!speakerOpions?.length) && (
        <div
          className="bg-[#fafafa] rounded-2xl flex-1"
          style={{ padding: "0 24px" }}
        >
          <Schedule
            alertSchedules={advancedAISetting?.virtualGuard?.schedules || []}
            setAlertSchedules={(schedules) => {
              setAdvancedAISetting({
                ...advancedAISetting,
                virtualGuard: {
                  ...advancedAISetting?.virtualGuard,
                  schedules,
                },
              });
            }}
          />
        </div>
      )}
    </div>
  );
}
